import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Heart, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VentLogo from './VentLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutPage = () => {
  const navigate = useNavigate();
  const [sageImage, setSageImage] = useState(null);

  const fetchSageImage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sage/image`);
      if (response.data.image_base64) {
        setSageImage(`data:image/png;base64,${response.data.image_base64}`);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:about]', err?.message);
    }
  }, []);

  useEffect(() => {
    fetchSageImage();
  }, [fetchSageImage]);

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="about-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">About</h1>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Sage Character */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-32 h-32 mx-auto mb-6"
            >
              <div className="absolute inset-0 bg-vent-primary/20 rounded-full blur-2xl" />
              {sageImage ? (
                <img
                  src={sageImage}
                  alt="The Universe Sage"
                  className="relative w-full h-full object-contain"
                />
              ) : (
                <div className="relative w-full h-full rounded-full bg-gradient-to-br from-vent-primary to-vent-secondary flex items-center justify-center">
                  <span className="text-3xl">🧙</span>
                </div>
              )}
            </motion.div>

            <VentLogo size="large" className="mb-4" />
            <p className="text-zinc-500 tracking-[0.2em] uppercase text-sm">
              Every human deserves to be heard
            </p>
          </motion.div>

          {/* About Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-3xl p-8 mb-8"
            data-testid="about-content"
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-vent-primary" />
              <h3 className="font-heading text-xl font-bold text-white">What is Vent?</h3>
            </div>
            
            <div className="space-y-4 text-zinc-400 leading-relaxed">
              <p>
                Vent is a gamified debate platform designed to counteract doomscrolling. 
                Every day, our mystical Sage—a personification of the Universe—presents 
                a thought-provoking topic for you to weigh in on.
              </p>
              
              <p>
                With just a single click, you can vote FOR or AGAINST, and if you're 
                feeling passionate, hit the VENT button to share your anonymous thoughts 
                with the community.
              </p>
              
              <p>
                No accounts. No judgment. Just pure, unfiltered debate.
              </p>
            </div>
          </motion.div>

          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-3xl p-8 mb-8"
          >
            <h3 className="font-heading text-xl font-bold text-white mb-6">How It Works</h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-vent-primary/20 flex items-center justify-center text-vent-primary font-bold shrink-0">
                  1
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Daily Topic</h4>
                  <p className="text-zinc-500 text-sm">The Sage presents a new debate topic every day</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-vent-secondary/20 flex items-center justify-center text-vent-secondary font-bold shrink-0">
                  2
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Cast Your Vote</h4>
                  <p className="text-zinc-500 text-sm">Click FOR or AGAINST to share your stance</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-vent-primary/20 flex items-center justify-center text-vent-primary font-bold shrink-0">
                  3
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Vent or Suggest</h4>
                  <p className="text-zinc-500 text-sm">Share anonymous thoughts on today's topic or suggest next week's — react with Hearts or Lightning</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-vent-secondary/20 flex items-center justify-center text-vent-secondary font-bold shrink-0">
                  4
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">See the Results</h4>
                  <p className="text-zinc-500 text-sm">Watch how the community voted as the day ends</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-3xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-vent-secondary" />
              <h3 className="font-heading text-xl font-bold text-white">Get In Touch</h3>
            </div>
            
            <p className="text-zinc-400 mb-6">
              Have feedback, ideas, or just want to say hi? We'd love to hear from you!
            </p>
            
            <a
              href="mailto:ventbackatus@gmail.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-vent-primary/20 text-vent-primary rounded-full font-bold hover:bg-vent-primary/30 transition-colors"
              data-testid="contact-email-btn"
            >
              <Mail className="w-4 h-4" />
              ventbackatus@gmail.com
            </a>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12 text-zinc-600 text-sm"
          >
            <p className="flex items-center justify-center gap-1">
              Made with <Heart className="w-4 h-4 text-red-500" fill="currentColor" /> by the Vent Team
            </p>
            <p className="mt-2">© 2026 Vent. All rights reserved.</p>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;
